import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosApi from "src/app/config";
import { getCurrentIndiaMonth } from "src/lib/dateUtils";

const initialState = {
  // KPI Cards
  kpiCards: null,
  kpiCardsLoading: false,
  kpiCardsError: null,
  
  // Boulder Consumption
  boulderConsumption: null,
  boulderConsumptionLoading: false,
  boulderConsumptionError: null,
  
  // Production Mix
  productionMix: null,
  productionMixLoading: false,
  productionMixError: null,
  
  // Cash Flow Analysis
  cashFlowAnalysis: null,
  cashFlowAnalysisLoading: false,
  cashFlowAnalysisError: null,
  
  // Client Performance
  clientPerformance: null,
  clientPerformanceLoading: false,
  clientPerformanceError: null,
  
  // Recent Activities
  recentActivities: null,
  recentActivitiesLoading: false,
  recentActivitiesError: null,
  
  // Pending Trips (Action Required)
  pendingTrips: null,
  pendingTripsLoading: false,
  pendingTripsError: null,
};


// Fetch KPI Cards data (New API)
export const fetchKpiCards = createAsyncThunk(
  "dashboard/fetchKpiCards",
  async ({ token, organizationId, month }, { rejectWithValue }) => {
    try {
      const response = await axiosApi({ token, organizationId }).get(
        `/dashboard/kpi-cards?month=${month || getCurrentIndiaMonth()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch Boulder Consumption data (New API)
export const fetchBoulderConsumption = createAsyncThunk(
  "dashboard/fetchBoulderConsumption",
  async ({ token, organizationId, month }, { rejectWithValue }) => {
    try {
      const response = await axiosApi({ token, organizationId }).get(
        `/dashboard/boulder-consumption?month=${month || getCurrentIndiaMonth()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch Production Mix data
export const fetchProductionMix = createAsyncThunk(
  "dashboard/fetchProductionMix",
  async ({ token, organizationId, month }, { rejectWithValue }) => {
    try {
      const response = await axiosApi({ token, organizationId }).get(
        `/dashboard/production-mix?month=${month || getCurrentIndiaMonth()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch Cash Flow Analysis data
export const fetchCashFlowAnalysis = createAsyncThunk(
  "dashboard/fetchCashFlowAnalysis",
  async ({ token, organizationId, month }, { rejectWithValue }) => {
    try {
      const response = await axiosApi({ token, organizationId }).get(
        `/dashboard/cash-flow-analysis?month=${month || getCurrentIndiaMonth()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch Client Performance data
export const fetchClientPerformance = createAsyncThunk(
  "dashboard/fetchClientPerformance",
  async ({ token, organizationId, month }, { rejectWithValue }) => {
    try {
      const response = await axiosApi({ token, organizationId }).get(
        `/dashboard/client-performance?month=${month || getCurrentIndiaMonth()}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch Recent Activities data
export const fetchRecentActivities = createAsyncThunk(
  "dashboard/fetchRecentActivities",
  async ({ token, organizationId, limit }, { rejectWithValue }) => {
    try {
      const response = await axiosApi({ token, organizationId }).get(
        `/dashboard/recent-activities?limit=${limit || 10}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch Pending Trips Count for Action Required component (optimized)
export const fetchPendingTripsCount = createAsyncThunk(
  "dashboard/fetchPendingTripsCount",
  async ({ token, organizationId }, { rejectWithValue }) => {
    try {
      const response = await axiosApi({ token, organizationId }).get(
        `/trips/pending-count`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    // Reset dashboard state
    resetDashboardState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // KPI Cards
    builder.addCase(fetchKpiCards.pending, (state) => {
      state.kpiCardsLoading = true;
      state.kpiCardsError = null;
    });
    builder.addCase(fetchKpiCards.fulfilled, (state, action) => {
      state.kpiCardsLoading = false;
      state.kpiCards = action.payload;
    });
    builder.addCase(fetchKpiCards.rejected, (state, action) => {
      state.kpiCardsLoading = false;
      state.kpiCardsError = action.payload;
    });
    
    // Boulder Consumption
    builder.addCase(fetchBoulderConsumption.pending, (state) => {
      state.boulderConsumptionLoading = true;
      state.boulderConsumptionError = null;
    });
    builder.addCase(fetchBoulderConsumption.fulfilled, (state, action) => {
      state.boulderConsumptionLoading = false;
      state.boulderConsumption = action.payload;
    });
    builder.addCase(fetchBoulderConsumption.rejected, (state, action) => {
      state.boulderConsumptionLoading = false;
      state.boulderConsumptionError = action.payload;
    });
    
    // Production Mix
    builder.addCase(fetchProductionMix.pending, (state) => {
      state.productionMixLoading = true;
      state.productionMixError = null;
    });
    builder.addCase(fetchProductionMix.fulfilled, (state, action) => {
      state.productionMixLoading = false;
      state.productionMix = action.payload;
    });
    builder.addCase(fetchProductionMix.rejected, (state, action) => {
      state.productionMixLoading = false;
      state.productionMixError = action.payload;
    });
    
    // Cash Flow Analysis
    builder.addCase(fetchCashFlowAnalysis.pending, (state) => {
      state.cashFlowAnalysisLoading = true;
      state.cashFlowAnalysisError = null;
    });
    builder.addCase(fetchCashFlowAnalysis.fulfilled, (state, action) => {
      state.cashFlowAnalysisLoading = false;
      state.cashFlowAnalysis = action.payload;
    });
    builder.addCase(fetchCashFlowAnalysis.rejected, (state, action) => {
      state.cashFlowAnalysisLoading = false;
      state.cashFlowAnalysisError = action.payload;
    });
    
    // Client Performance
    builder.addCase(fetchClientPerformance.pending, (state) => {
      state.clientPerformanceLoading = true;
      state.clientPerformanceError = null;
    });
    builder.addCase(fetchClientPerformance.fulfilled, (state, action) => {
      state.clientPerformanceLoading = false;
      state.clientPerformance = action.payload;
    });
    builder.addCase(fetchClientPerformance.rejected, (state, action) => {
      state.clientPerformanceLoading = false;
      state.clientPerformanceError = action.payload;
    });
    
    // Recent Activities
    builder.addCase(fetchRecentActivities.pending, (state) => {
      state.recentActivitiesLoading = true;
      state.recentActivitiesError = null;
    });
    builder.addCase(fetchRecentActivities.fulfilled, (state, action) => {
      state.recentActivitiesLoading = false;
      state.recentActivities = action.payload;
    });
    builder.addCase(fetchRecentActivities.rejected, (state, action) => {
      state.recentActivitiesLoading = false;
      state.recentActivitiesError = action.payload;
    });

    // Pending Trips Count
    builder.addCase(fetchPendingTripsCount.pending, (state) => {
      state.pendingTripsLoading = true;
      state.pendingTripsError = null;
    });
    builder.addCase(fetchPendingTripsCount.fulfilled, (state, action) => {
      state.pendingTripsLoading = false;
      state.pendingTrips = action.payload;
    });
    builder.addCase(fetchPendingTripsCount.rejected, (state, action) => {
      state.pendingTripsLoading = false;
      state.pendingTripsError = action.payload;
    });
  },
});

export const { resetDashboardState } = dashboardSlice.actions;
export default dashboardSlice.reducer;